/**
 * Contrato da solicitação de compra feita pelo funcionário.
 *
 * Papel: aqui ficam o formulário de cinco campos que o funcionário envia e o
 * formato de cada linha do histórico que ele vê depois de enviar.
 *
 * Como usar: `novaSolicitacaoSchema` é estrito. Um campo fora dos cinco, como
 * `status` ou `aprovador`, é recusado com erro, e não descartado em silêncio:
 * quem monta o corpo fora do formulário fica sabendo que mandou algo que não
 * será gravado. Status, categoria, canal, residência e número do pedido não
 * vêm do corpo; a API os define no servidor.
 *
 * Quantidade e valor aceitam texto com máscara, como o campo da tela entrega,
 * pelas mesmas regras de `novoItemPedidoSchema`. Valor zero ou negativo é
 * recusado aqui, e não só na tela: uma solicitação de preço zero chegaria ao
 * quadro de Compras sem que ninguém percebesse.
 */
import { z } from 'zod';

import { idSchema, textoObrigatorio } from './comum';
import { fotoDeEntradaDoPedidoSchema } from './compras';
import { valorPorUnidadeSchema } from './formatos';

/**
 * O que o funcionário envia para pedir uma compra.
 *
 * `foto` é opcional e segue o limite de `fotoDeEntradaDoPedidoSchema`, a mesma
 * imagem em base64 que o formulário de pedido do escritório aceita.
 */
export const novaSolicitacaoSchema = z.strictObject({
  descricao: textoObrigatorio('Descrição', 300),
  fornecedor: textoObrigatorio('Fornecedor', 120),
  quantidade: z
    .union([z.number(), z.string()])
    .transform((valor) => (typeof valor === 'number' ? valor : Number(valor.trim())))
    .refine((valor) => Number.isInteger(valor) && valor >= 1, {
      message: 'A quantidade precisa ser um número inteiro de ao menos 1.',
    }),
  valorUnitario: valorPorUnidadeSchema.refine((valor) => valor > 0, {
    message: 'O valor unitário precisa ser maior que zero.',
  }),
  foto: fotoDeEntradaDoPedidoSchema,
});

export type NovaSolicitacao = z.infer<typeof novaSolicitacaoSchema>;

/**
 * Uma linha do histórico do funcionário, como a listagem a devolve.
 *
 * `criadoEm` é o instante em que a solicitação foi gravada, com fuso, para a
 * tela mostrar data e hora na renderização. `temFoto` diz só se há imagem: a
 * foto não vem na listagem, e a tela a busca pela rota própria quando
 * `temFoto` é verdadeiro.
 */
export const solicitacaoDoHistoricoSchema = z.object({
  id: idSchema,
  numero: z.string().min(1),
  criadoEm: z.iso.datetime({ offset: true }),
  descricao: z.string(),
  fornecedor: z.string(),
  quantidade: z.number().int().min(1),
  valorUnitario: z.number(),
  temFoto: z.boolean(),
});

export type SolicitacaoDoHistorico = z.infer<typeof solicitacaoDoHistoricoSchema>;
